import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authService, paymentService, userSessionService, appointmentService } from '../../services';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import './PaymentResult.css';

export default function PaymentResult() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('Đang kiểm tra thanh toán...');
  const [statusType, setStatusType] = useState('loading'); // loading, success, error, warning
  const [orderInfo, setOrderInfo] = useState(null);

  useEffect(() => {
    let orderId = searchParams.get('orderId');
    const orderCode = searchParams.get('orderCode');
    if (!orderId) {
      orderId = localStorage.getItem('pendingOrderId');
    }

    // Single start flow: map PayOS orderCode to internal orderId (if possible), then run the existing polling and appointment creation
    const start = async () => {
      // attempt mapping
      if (!orderId && orderCode) {
        try {
          try {
            await fetch(`/api/payments/success?orderCode=${encodeURIComponent(orderCode)}&status=${encodeURIComponent(searchParams.get('status') || '')}`);
          } catch (e) {
            console.warn('Failed to call backend success endpoint (non-fatal):', e);
          }

          const mapRes = await paymentService.getOrderIdByPayOS(orderCode);
          if (mapRes && mapRes.success && mapRes.orderId) {
            orderId = mapRes.orderId;
            try { localStorage.setItem('pendingOrderId', orderId); } catch (e) {}
          }
        } catch (err) {
          console.warn('Unable to map orderCode to internal orderId', err);
        }
      }
      // If we have pending appointment info, try server-side simulate-complete first (sets pending context and triggers server processing)
      const scheduledForValue = localStorage.getItem('pendingScheduledFor') || null;
      const therapistId = localStorage.getItem('pendingTherapistId') || null;

      let simulatedAppointment = null;
      if ((therapistId || scheduledForValue) && orderId) {
        try {
          const currentUserQuick = authService.getCurrentUser();
          if (currentUserQuick && currentUserQuick.id) {
            setStatus('Đang hoàn tất đặt lịch trên máy chủ...');
            setStatusType('loading');
            const simRes = await paymentService.simulateComplete(orderId, {
              TherapistId: therapistId || null,
              ScheduledFor: scheduledForValue || null
            });
            // allow server a moment to process before continuing to poll
            await new Promise(r => setTimeout(r, 500));
            if (simRes && simRes.success && simRes.appointment) {
              simulatedAppointment = simRes.appointment;
              // If appointment was created by server, show meeting URL immediately and finish
              if (simulatedAppointment.meetingUrl) {
                setStatus('Đặt lịch đã được xác nhận! Đang chuyển hướng...');
                setStatusType('success');
                setOrderInfo({
                  orderId: orderId,
                  meetingUrl: simulatedAppointment.meetingUrl
                });
                try {
                  localStorage.removeItem('pendingOrderId');
                  localStorage.removeItem('pendingScheduledFor');
                  localStorage.removeItem('pendingTherapistId');
                } catch (e) {}
                setTimeout(() => navigate('/appointment-calendar'), 2000);
                return; // end flow early
              }
            }
          }
        } catch (simEx) {
          console.warn('simulateComplete failed (non-fatal), falling back to client polling:', simEx);
        }
      }

      // proceed with original logic
      if (!orderId) {
        setStatus('Thiếu thông tin đơn hàng. Vui lòng liên hệ hỗ trợ.');
        setStatusType('error');
        return;
      }

      const currentUser = authService.getCurrentUser();
      if (!currentUser?.id) {
        setStatus('Vui lòng đăng nhập để hoàn tất đặt lịch');
        setStatusType('warning');
        // redirect to login keeping return
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      let attempts = 0;
      const maxAttempts = 8;

      const check = async () => {
        try {
          setStatus('Đang kiểm tra trạng thái thanh toán...');
          setStatusType('loading');
          const res = await paymentService.checkPaymentStatus(orderId);
          // res structure from backend: success, message, data
          if (res?.success && res?.data?.paymentStatus === 'COMPLETED') {
            setStatus('Thanh toán thành công! Đang hoàn tất đặt lịch...');
            setStatusType('loading');

            // Backend should have created user sessions for this order. Fetch sessions and pick one to create appointment.
            const sessions = await userSessionService.getUserSessionsByUser(currentUser.id);
            const sessionForOrder = (sessions || []).find(s => String(s.orderId || s.OrderId) === String(orderId) || String(s.orderId || s.OrderId) === String(res?.data?.orderId));

            let userSessionId = sessionForOrder?.id || sessionForOrder?.Id || sessionForOrder?.Id;
            if (!userSessionId && sessions && sessions.length > 0) {
              // fallback: pick first available session
              const anyAvailable = sessions.find(s => (s.statusValue === 0 || s.StatusValue === 0));
              userSessionId = anyAvailable?.id || anyAvailable?.Id;
            }

            if (!userSessionId) {
              setStatus('Thanh toán đã được ghi nhận nhưng không tìm thấy phiên người dùng. Vui lòng liên hệ hỗ trợ.');
              setStatusType('error');
              return;
            }

            // If server already created appointment during simulate, skip client-side create
            if (simulatedAppointment) {
              setStatus('Đặt lịch đã được xác nhận! Đang chuyển hướng...');
              setStatusType('success');
              const scheduledFor = localStorage.getItem('pendingScheduledFor');
              setOrderInfo({
                orderId: orderId,
                scheduledFor: scheduledFor || 'Đã lên lịch'
              });
              try {
                localStorage.removeItem('pendingOrderId');
                localStorage.removeItem('pendingScheduledFor');
                localStorage.removeItem('pendingTherapistId');
              } catch (e) {}
              setTimeout(() => navigate('/appointment-calendar'), 1500);
              return;
            }

            // Create appointment using same logic as booking confirmation
            const scheduledForValue = localStorage.getItem('pendingScheduledFor') || null;
            const therapistId = localStorage.getItem('pendingTherapistId') || null;

            if (!therapistId && !(res && res.data && res.data.therapistId)) {
              // Log a friendly warning — without a therapist id we'll still attempt to create
              // the appointment but the server may reject or treat therapist as optional.
              console.warn('PaymentResult: missing therapistId in localStorage and payment response. Appointment may need manual handling.');
            }

            // Backend expects snake_case JSON. Build snake_case payload for appointment creation
            const appointmentPayload = {
              therapist_id: therapistId || (res && res.data && res.data.therapistId) || null,
              client_id: currentUser.id,
              user_session_id: userSessionId,
              scheduled_for: scheduledForValue || new Date().toISOString(),
              duration_minutes: 60,
              status_value: 0,
              meeting_url: null,
              created_at: new Date().toISOString()
            };

            // If the server returned package info attach it to payload so backend can use it if needed
            if (res && res.data && res.data.therapyPackageId) {
              appointmentPayload.therapy_package_id = res.data.therapyPackageId;
            }

            console.log('PaymentResult - creating appointment with payload:', appointmentPayload);
            await appointmentService.createAppointment(appointmentPayload);

            setStatus('Đặt lịch đã được xác nhận! Đang chuyển hướng...');
            setStatusType('success');
            const scheduledFor = scheduledForValue || localStorage.getItem('pendingScheduledFor');
            setOrderInfo({
              orderId: orderId,
              scheduledFor: scheduledFor || 'Đã lên lịch',
              therapistId: therapistId
            });
            // cleanup pending data
            try {
              localStorage.removeItem('pendingOrderId');
              localStorage.removeItem('pendingScheduledFor');
              localStorage.removeItem('pendingTherapistId');
            } catch (e) {}

            setTimeout(() => navigate('/appointment-calendar'), 1500);
            return;
          }

          // Not paid yet
          attempts++;
          if (attempts >= maxAttempts) {
            setStatus('Thanh toán chưa hoàn tất. Bạn có thể quay lại trang đặt lịch hoặc thử lại.');
            setStatusType('warning');
            return;
          }

          // Wait and poll again
          setStatus('Thanh toán chưa hoàn tất. Đang thử lại...');
          setStatusType('loading');
          setTimeout(check, 3000);
        } catch (err) {
          console.error('Error checking payment status', err);
          attempts++;
          if (attempts >= maxAttempts) {
            setStatus('Lỗi khi kiểm tra trạng thái thanh toán. Vui lòng liên hệ hỗ trợ.');
            setStatusType('error');
            return;
          }
          setStatus('Đang thử lại...');
          setStatusType('loading');
          setTimeout(check, 3000);
        }
      };

      // start polling
      check();
    };

    // Start the flow
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  const getStatusIcon = () => {
    switch (statusType) {
      case 'loading':
        return (
          <div className="status-icon loading">
            <div className="loading-spinner"></div>
          </div>
        );
      case 'success':
        return (
          <div className="status-icon success">
            <div className="success-checkmark"></div>
          </div>
        );
      case 'error':
        return (
          <div className="status-icon error">
            <span>✕</span>
          </div>
        );
      case 'warning':
        return (
          <div className="status-icon warning">
            <span>⚠</span>
          </div>
        );
      default:
        return (
          <div className="status-icon loading">
            <div className="loading-spinner"></div>
          </div>
        );
    }
  };

  const getStatusMessageClass = () => {
    return `payment-status-message ${statusType}`;
  };

  return (
    <div className="payment-result page-with-header">
      <Header />
      <div className="payment-result-container">
        <div className="payment-result-card">
          <div className="status-icon-container">
            {getStatusIcon()}
          </div>

          <h1 className="payment-result-title">
            {statusType === 'success' ? 'Thanh toán thành công!' : 
             statusType === 'error' ? 'Có lỗi xảy ra' :
             statusType === 'warning' ? 'Đang xử lý' :
             'Đang kiểm tra thanh toán'}
          </h1>

          <div className={getStatusMessageClass()}>
            {status}
          </div>

          {statusType === 'loading' && (
            <div className="progress-container loading">
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
            </div>
          )}

          {statusType === 'success' && orderInfo && (
            <div className="success-details">
              <div className="success-details-title">
                <span>✓</span>
                <span>Thông tin đặt lịch</span>
              </div>
              {orderInfo.therapistName && (
                <div className="success-details-item">
                  <span className="success-details-label">Chuyên gia:</span>
                  <span className="success-details-value">{orderInfo.therapistName}</span>
                </div>
              )}
              {orderInfo.scheduledFor && (
                <div className="success-details-item">
                  <span className="success-details-label">Thời gian:</span>
                  <span className="success-details-value">{orderInfo.scheduledFor}</span>
                </div>
              )}
              {orderInfo.orderId && (
                <div className="success-details-item">
                  <span className="success-details-label">Mã đơn hàng:</span>
                  <span className="success-details-value">#{orderInfo.orderId.slice(-8)}</span>
                </div>
              )}
            </div>
          )}

          {statusType === 'error' && (
            <div className="error-message">
              <div className="error-message-title">Lỗi thanh toán</div>
              <div className="error-message-text">
                Vui lòng liên hệ hỗ trợ nếu bạn đã thanh toán thành công.
              </div>
            </div>
          )}

          <div className="payment-actions">
            {statusType === 'success' ? (
              <button 
                className="payment-btn payment-btn-primary"
                onClick={() => navigate('/appointment-calendar')}
              >
                Xem lịch hẹn
              </button>
            ) : (
              <>
                <button 
                  className="payment-btn payment-btn-primary"
                  onClick={() => navigate('/booking-dashboard')}
                >
                  Quay lại đặt lịch
                </button>
                <button 
                  className="payment-btn payment-btn-secondary"
                  onClick={() => navigate('/')}
                >
                  Về trang chủ
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
